import { Injectable } from '@angular/core';
import { readFromStorage, writeToStorage } from '../shared/utils/local-storage.utils';
import { ReleaseNotesModalData, ReleaseNotesService, ReleaseNote } from '@healthcatalyst/cashmere';
import { MarkdownService } from 'ngx-markdown';
import { ReleaseNote as AdminReleaseNote } from '../shared/models/release-note';

export interface ReleaseNotes {
    version: string;
    releaseMonth: number;
    releaseYear: number;
    releaseDay: number;
    notesHTML: string;
}

@Injectable({ providedIn: 'root' })
export class ReleaseNotesModalService {
    readonly _lastReadReleaseNoteStorageKey = 'CDP_LAST_READ_RELEASE_NOTE_DATE';
    public hasNewReleaseNotes = false;

    constructor(
        private releaseNotesService: ReleaseNotesService,
        private markdownService: MarkdownService
    ) {}

    async transformAndOpenAdminReleaseNotes(releaseNotes: AdminReleaseNote[]): Promise<void> {
        if (!releaseNotes || releaseNotes.length === 0) {
            console.warn('No release notes data available');
            return;
        }

        const modalData: ReleaseNotesModalData = {
            appName: 'Ignite',
            headerLinkUrl: 'https://docs.healthcatalyst.com/ignite/',
            headerLinkText: 'Read the docs',
            releaseNotes: await Promise.all(
                releaseNotes
                    .filter(note => note.activeFlag === true)
                    .map(async (releaseNote, index) => {
                        const releaseDate = new Date(releaseNote.releaseDate);
                        return {
                            version: releaseNote.version,
                            notesHTML: await this.markdownService.parse(releaseNote.content),
                            releaseMonth: releaseDate.getMonth() + 1,
                            releaseYear: releaseDate.getFullYear(),
                            releaseDay: releaseDate.getDate(),
                            isOpen: index === 0
                        } as ReleaseNote;
                    })
            )
        };

        this.openAdminReleaseNotes(modalData);
    }

    // Check admin-managed release notes for new content and update flag
    checkForNewAdminReleaseNotes(adminReleaseNotes: AdminReleaseNote[]): void {
        if (!adminReleaseNotes || adminReleaseNotes.length === 0) {
            return;
        }
        const rawLastRead = readFromStorage(this._lastReadReleaseNoteStorageKey);
        const lastReadReleaseNoteDate = new Date(rawLastRead || 0);

        const activeReleaseNotes = adminReleaseNotes.filter(note => note.activeFlag === true);
        if (activeReleaseNotes.length === 0) {
            return;
        }

        const latestReleaseDate = activeReleaseNotes
            .map(r => new Date(r.releaseDate))
            .sort((a, b) => b.getTime() - a.getTime())[0];

        if (lastReadReleaseNoteDate < latestReleaseDate) {
            this.hasNewReleaseNotes = true;
        }
    }

    openAdminReleaseNotes(releaseNotesData: ReleaseNotesModalData): void {
        this.releaseNotesService.open(releaseNotesData, {});
        writeToStorage(this._lastReadReleaseNoteStorageKey, new Date().toString());
        this.hasNewReleaseNotes = false;
    }
}
